/**
 * Morning Gazette — news feed combining:
 *   1. Active crises (Phase 3 M1): countdown timer + counter-action buttons
 *   2. Reactive reports (auto-generated from major actions): resolved state
 *   3. Flavor entries (legacy static templates): unchanged presentation
 *
 * See corpllm-specs/specs/SPEC_crisis_events.md
 */
import { state } from '../main';
import { fixture } from '../data/fixture';
import { t } from '../i18n';
import { crisisRespond } from '../api';

// labelKey, not label: these badges used to hold literal German ('WARNUNG',
// 'CHANCE'), which is what English players saw regardless of their language.
const TYPE_STYLE: Record<string, { border: string; badge: string; labelKey: string }> = {
  event:       { border: 'var(--red)',     badge: 'rgba(231,76,60,0.12)',   labelKey: 'gazette.type_event' },
  intel:       { border: 'var(--cyan)',    badge: 'rgba(var(--ac),0.1)',    labelKey: 'gazette.type_intel' },
  warning:     { border: 'var(--amber)',   badge: 'rgba(252,163,17,0.12)',  labelKey: 'gazette.type_warning' },
  opportunity: { border: 'var(--green)',   badge: 'rgba(46,204,113,0.12)', labelKey: 'gazette.type_opportunity' },
};

/** Counter-action labels (i18n keys for button text). */
const COUNTER_ACTION_LABELS: Record<string, string> = {
  bribe_journalist:   'gazette.counter.bribe_journalist',
  legal_defense:      'gazette.counter.legal_defense',
  strike_busting:     'gazette.counter.strike_busting',
  community_donation: 'gazette.counter.community_donation',
};

export function renderGazette(): HTMLElement {
  const articles = fixture.gazette ?? [];

  const root = document.createElement('div');
  root.className = 'screen';

  const style = document.createElement('style');
  style.textContent = `
    .gazette-header { text-align:center; padding:24px 0 16px; border-bottom:2px solid var(--border); margin-bottom:24px; }
    .gazette-masthead { font-family:var(--display); font-size:36px; font-weight:700; color:var(--text-hi); letter-spacing:6px; text-transform:uppercase; }
    .gazette-date { font-family:var(--mono); font-size:11px; color:var(--text-lo); letter-spacing:2px; margin-top:6px; }
    .gazette-tagline { font-family:var(--mono); font-size:12px; color:var(--text-lo); margin-top:4px; font-style:italic; }
    .article { background:var(--surface-frost); backdrop-filter:blur(3px); -webkit-backdrop-filter:blur(3px); border:1px solid var(--line); border-radius:4px; padding:16px; margin-bottom:16px; border-left-width:3px; }
    .article-meta { display:flex; align-items:center; gap:8px; margin-bottom:8px; }
    .article-cat { font-family:var(--mono); font-size:11px; letter-spacing:2px; text-transform:uppercase; color:var(--text-lo); }
    .article-date { font-family:var(--mono); font-size:11px; color:var(--text-lo); }
    .article-type-badge { font-family:var(--mono); font-size:11px; padding:2px 7px; border-radius:3px; }
    .article h2 { font-family:var(--display); font-size:18px; font-weight:700; color:var(--text-hi); margin-bottom:8px; line-height:1.3; }
    .article .lead { font-size:13px; color:var(--text-mid); margin-bottom:8px; font-style:italic; line-height:1.5; }
    .article .body { font-size:13px; color:var(--text-mid); line-height:1.6; margin-bottom:8px; }
    .article .impact { font-family:var(--mono); font-size:11px; padding:6px 10px; border-radius:3px; background:rgba(0,0,0,0.3); }
    /* Phase 3 M1 Crisis styling */
    .crisis-card { border-left-color:var(--red,#e63946) !important; border-left-width:4px !important; }
    .crisis-resolved { opacity:0.65; border-left-color:var(--text-lo,#888) !important; }
    .crisis-report { border-left-color:var(--amber,#fa3) !important; }
    .crisis-timer { display:inline-block; padding:2px 8px; border-radius:3px; background:rgba(230,57,70,0.15); color:var(--red,#e63946); font-family:var(--mono,monospace); font-size:12px; margin-left:auto; }
    .crisis-timer-urgent { animation:crisis-pulse 1s infinite; }
    @keyframes crisis-pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }
    .crisis-effect-preview { margin-top:6px; padding:6px 10px; background:rgba(230,57,70,0.08); border-left:2px solid var(--red,#e63946); font-family:var(--mono,monospace); font-size:11px; color:var(--red,#e63946); }
    .crisis-counter-btns { display:flex; flex-wrap:wrap; gap:6px; margin-top:10px; }
    .crisis-counter-btn { background:var(--red,#e63946); border:0; color:#fff; padding:6px 12px; font-family:var(--mono,monospace); font-size:12px; letter-spacing:1px; border-radius:3px; cursor:pointer; text-transform:uppercase; }
    .crisis-counter-btn:hover:not(:disabled) { background:#c32f3c; }
    .crisis-counter-btn:disabled { opacity:0.4; cursor:not-allowed; }
    .crisis-responded-badge { display:inline-block; padding:3px 10px; background:rgba(6,214,160,0.12); color:var(--green,#06d6a0); font-family:var(--mono,monospace); font-size:11px; border-radius:3px; margin-top:6px; }
    .crisis-resolved-badge { display:inline-block; padding:3px 10px; background:rgba(136,136,136,0.15); color:var(--text-lo,#888); font-family:var(--mono,monospace); font-size:11px; border-radius:3px; margin-top:6px; }
  `;
  root.appendChild(style);

  const currentDay = fixture.day ?? 0;
  // The engine stamps every entry with the current day as German prose
  // ("Tag 7", mapper.go buildGazette/buildGazetteWithCrises), so a.date is not
  // usable in an English UI. The day is already in the fixture — render it here
  // instead and let i18n format it.
  const articleDate = t('gazette.article_day', { day: String(currentDay) });

  const articlesHtml = articles.map((a, idx) => {
    const ts = TYPE_STYLE[a.type] ?? TYPE_STYLE.event;

    // Phase 3 M1: crisis-specific rendering.
    if (a.crisisId) {
      return renderCrisisCard(a, idx, currentDay);
    }

    // Flavor entry. headline/lead/body/impact are i18n keys from the server
    // (gazette.ambient.*) — run them through t(). t() is identity for any legacy
    // plain string, so this is safe for non-keyed entries too.
    return `<div class="article" style="border-left-color:${ts.border}">
      <div class="article-meta">
        <span class="article-cat">${a.category}</span>
        <span style="color:var(--text-lo)">·</span>
        <span class="article-date">${articleDate}</span>
        <span style="flex:1"></span>
        <span class="article-type-badge" style="background:${ts.badge}; border:1px solid ${ts.border}55; color:${ts.border}">${t(ts.labelKey)}</span>
      </div>
      <h2>${t(a.headline ?? '')}</h2>
      <p class="lead">${t(a.lead ?? '')}</p>
      <p class="body">${t(a.body ?? '')}</p>
      <div class="impact" style="color:${ts.border}">⚡ ${t(a.impact ?? '')}</div>
    </div>`;
  }).join('');

  const day = fixture.day;

  root.innerHTML += `
    <div style="max-width:900px; margin:0 auto">
      <div class="gazette-header">
        <div class="gazette-masthead">${t('gazette.masthead')}</div>
        <div class="gazette-date">${t('gazette.date_line', { day: String(day), issue: String(day) })}</div>
        <div class="gazette-tagline">${t('gazette.tagline')}</div>
      </div>

      ${articlesHtml}
    </div>
  `;

  // Wire up crisis-counter buttons.
  root.querySelectorAll<HTMLButtonElement>('.crisis-counter-btn').forEach(btn => {
    btn.addEventListener('click', async () => {
      if (btn.disabled) return;
      const crisisId = btn.dataset.crisisId;
      const actionId = btn.dataset.actionId;
      if (!crisisId || !actionId) return;
      const oldText = btn.textContent;
      btn.textContent = '...';
      btn.disabled = true;
      try {
        await crisisRespond(state.sessionId, crisisId, { actionId });
        btn.textContent = t('gazette.counter.responded_ok');
      } catch (e: unknown) {
        btn.textContent = t('gazette.counter.responded_fail');
        console.error('[gazette] crisis respond failed:', (e instanceof Error ? e.message : String(e)));
        setTimeout(() => {
          btn.textContent = oldText;
          btn.disabled = false;
        }, 2000);
      }
    });
  });

  return root;
}

/** Render a crisis card (with timer + counter-buttons) or a reactive report
 *  (resolved styling). */
function renderCrisisCard(a: any, _idx: number, currentDay: number): string {
  const isReport = a.isReport === true;
  const resolved = a.resolved === true;
  const responded = a.responded === true;

  const extraClasses = [
    'crisis-card',
    isReport ? 'crisis-report' : '',
    resolved && !isReport ? 'crisis-resolved' : '',
  ].filter(Boolean).join(' ');

  const borderColor = isReport
    ? 'var(--amber,#fa3)'
    : (resolved ? 'var(--text-lo,#888)' : 'var(--red,#e63946)');

  let timerHTML = '';
  if (!isReport && !resolved && a.deadlineDay) {
    const daysLeft = a.deadlineDay - currentDay;
    const urgent = daysLeft <= 1;
    const urgentClass = urgent ? ' crisis-timer-urgent' : '';
    timerHTML = `<span class="crisis-timer${urgentClass}">${t('gazette.crisis_timer', { days: String(Math.max(0, daysLeft)) })}</span>`;
  }

  let effectHTML = '';
  if (a.effectPreview && !resolved) {
    effectHTML = `<div class="crisis-effect-preview">${t(a.effectPreview)}</div>`;
  }

  let buttonsHTML = '';
  if (!isReport && !resolved && !responded && Array.isArray(a.counterActions) && a.counterActions.length > 0) {
    const btns = a.counterActions.map((ca: string) => {
      const labelKey = COUNTER_ACTION_LABELS[ca] ?? ca;
      return `<button class="crisis-counter-btn" data-crisis-id="${a.crisisId}" data-action-id="${ca}">${t(labelKey)}</button>`;
    }).join('');
    buttonsHTML = `<div class="crisis-counter-btns">${btns}</div>`;
  }

  let statusBadgeHTML = '';
  if (responded && !resolved) {
    statusBadgeHTML = `<div class="crisis-responded-badge">✓ ${t('gazette.crisis_responded')}</div>`;
  } else if (resolved && !isReport) {
    statusBadgeHTML = `<div class="crisis-resolved-badge">${t('gazette.crisis_resolved')}</div>`;
  }

  // Headline and body go through i18n interpolation with context if available.
  // headlineVars carries {corp}/{sector} from the server so "…in Sektor {sector}"
  // resolves instead of leaking the raw placeholder.
  const headline = t(a.headline ?? '', a.headlineVars);
  const body = a.body ? t(a.body, a.headlineVars) : '';

  const typeLabel = isReport ? t('gazette.type_report') : t('gazette.type_crisis');

  return `<div class="article ${extraClasses}" style="border-left-color:${borderColor}">
    <div class="article-meta">
      <span class="article-cat">${a.category}</span>
      <span style="color:var(--text-lo)">·</span>
      <span class="article-date">${t('gazette.article_day', { day: String(currentDay) })}</span>
      <span class="article-type-badge" style="background:rgba(230,57,70,0.12); border:1px solid ${borderColor}55; color:${borderColor}">${typeLabel}</span>
      ${timerHTML}
    </div>
    <h2>${headline}</h2>
    ${body ? `<p class="body">${body}</p>` : ''}
    ${effectHTML}
    ${buttonsHTML}
    ${statusBadgeHTML}
  </div>`;
}
